import React, { useEffect, useRef, useState, useCallback } from "react";

/* ================= STORAGE KEY ================= */

const LANG_KEY = "preferredLanguage";

/* ================= COMPONENT ================= */

const TranslationToggle = () => {
  const [isMarathi, setIsMarathi] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);

  const observerRef = useRef(null);
  const debounceRef = useRef(null);
  const translatingRef = useRef(false);

  const BHASHINI_URL =
    "https://dhruva-api.bhashini.gov.in/services/inference/pipeline";

  const AUTH_KEY =
    "llicdmBJGzIyxiIWICY4itm5FIkIzv-C3F2WomjbPGxT79oynHNF24EmABpYTFSE";

  /* ================= TEXT COLLECTION ================= */

  const isApplicationOrReferenceCode = (text) => {
    const trimmed = String(text || "").trim();

    if (!trimmed || /\s/.test(trimmed) || /[a-z]/.test(trimmed)) {
      return false;
    }

    if (trimmed.length < 8) {
      return false;
    }

    return /\d/.test(trimmed) && /[A-Z]/.test(trimmed);
  };

  const getPageTextNodes = () => {
    const rootToIgnore = document.getElementById("translation-toggle-root");

    const walker = document.createTreeWalker(
      document.body,
      NodeFilter.SHOW_TEXT,
      {
        acceptNode(node) {
          if (!node.nodeValue || !node.nodeValue.trim()) {
            return NodeFilter.FILTER_REJECT;
          }

          const parent = node.parentElement;
          if (!parent) return NodeFilter.FILTER_REJECT;

          
          if (rootToIgnore && rootToIgnore.contains(parent)) {
            return NodeFilter.FILTER_REJECT;
          }

          
          if (
            parent.closest(`
              input,
              textarea,
              select,
              option,
              [contenteditable],
              script,
              style,
              noscript,
              iframe
            `)
          ) {
            return NodeFilter.FILTER_REJECT;
          }

         
          if (
            parent.closest(`
              [id^="react-select-"],
              [class*="placeholder"],
              [class*="css-"],
              [role="combobox"],
              [aria-autocomplete="list"],
              [data-no-translate]
            `)
          ) {
            return NodeFilter.FILTER_REJECT;
          }

          if (node.__translated) {
            return NodeFilter.FILTER_REJECT;
          }

          if (isApplicationOrReferenceCode(node.nodeValue)) {
            return NodeFilter.FILTER_REJECT;
          }

          return NodeFilter.FILTER_ACCEPT;
        }
      }
    );

    const nodes = [];
    let current;
    while ((current = walker.nextNode())) {
      nodes.push(current);
    }

    return nodes;
  };

  /* ================= API TRANSLATION ================= */

  const batchTranslate = async (texts) => {
    try {
      const res = await fetch(BHASHINI_URL, {
        method: "POST",
        headers: {
          Authorization: AUTH_KEY,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          pipelineTasks: [
            {
              taskType: "translation",
              config: {
                language: {
                  sourceLanguage: "en",
                  targetLanguage: "mr"
                },
                "postProcessors": [
                    "glossary"
                ]
              }
            }
          ],
          inputData: {
            input: texts.map((t) => ({ source: t }))
          }
        })
      });

      const json = await res.json();

      return (
        json?.pipelineResponse?.[0]?.output?.map((o) => o.target) || texts
      );
    } catch (e) {
      console.error("Translation error", e);
      return texts;
    }
  };

  /* ================= TRANSLATION CORE ================= */

  const translatePage = useCallback(
    async (force = false) => {
      if (!isMarathi) return;
      if (translatingRef.current && !force) return;

      translatingRef.current = true;
      setIsTranslating(true);

      const nodes = getPageTextNodes();
      const texts = nodes.map((n) => n.nodeValue);

      if (!texts.length) {
        translatingRef.current = false;
        setIsTranslating(false);
        return;
      }

      const translated = await batchTranslate(texts);

      nodes.forEach((node, i) => {
        if (node.isConnected && translated[i]) {
          node.nodeValue = translated[i];
          node.__translated = true;
        }
      });

      translatingRef.current = false;
      setIsTranslating(false);
    },
    [isMarathi]
  );

  /* ================= MUTATION OBSERVER ================= */

  const startObserver = useCallback(() => {
    if (observerRef.current) return;

    observerRef.current = new MutationObserver(() => {
      if (!isMarathi) return;

      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        translatePage();
      }, 300);
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }, [isMarathi, translatePage]);

  /* ================= TOGGLE HANDLER ================= */

  const handleToggle = () => {
    const next = !isMarathi;
    setIsMarathi(next);
    localStorage.setItem(LANG_KEY, next ? "mr" : "en");

    if (!next) {
      observerRef.current?.disconnect();
      observerRef.current = null;
      window.location.reload(); // safest revert
    }
  };

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    if (localStorage.getItem(LANG_KEY) === "mr") {
      setIsMarathi(true);
    }
  }, []);

  /* ================= ENABLE TRANSLATION ================= */

  useEffect(() => {
    if (!isMarathi) return;

    startObserver();
    setTimeout(() => translatePage(true), 400);
  }, [isMarathi, startObserver, translatePage]);

  /* ================= LIFERAY SPA FIX ================= */

  useEffect(() => {
    const handler = () => {
      if (!isMarathi) return;

      let tries = 0;
      const retry = () => {
        translatePage(true);
        tries++;
        if (tries < 5) setTimeout(retry, 400);
      };

      setTimeout(retry, 300);
    };

    window.Liferay?.on("endNavigate", handler);

    return () => {
      window.Liferay?.detach("endNavigate", handler);
    };
  }, [isMarathi, translatePage]);

  /* ================= UI ================= */

  return (
    <div id="translation-toggle-root" className="d-flex align-items-center">
      <div className="toggle-switch">
        <input
          type="checkbox"
          checked={isMarathi}
          onChange={handleToggle}
          disabled={isTranslating}
        />
        <span className="slider" />
      </div>

      <span className="ms-2">
        {isTranslating ? "Translating…" : isMarathi ? "मराठी" : "English"}
      </span>

      <style>{`
        .toggle-switch {
          position: relative;
          width: 44px;
          height: 22px;
        }
        .toggle-switch input {
          opacity: 0;
          width: 44px;
          height: 22px;
          position: absolute;
          z-index: 2;
          cursor: pointer;
        }
        .slider {
          position: absolute;
          inset: 0;
          background: #ccc;
          border-radius: 34px;
        }
        .slider:before {
          content: "";
          position: absolute;
          height: 18px;
          width: 18px;
          left: 2px;
          bottom: 2px;
          background: #fff;
          border-radius: 50%;
          transition: 0.3s;
        }
        input:checked + .slider {
          background: #0d6efd;
        }
        input:checked + .slider:before {
          transform: translateX(22px);
        }
      `}</style>
    </div>
  );
};

export default TranslationToggle;
